import type { CSSProperties, FC, ReactNode } from 'react';
import clsx from 'clsx';

import './BayBadge.scss';

import BayTooltip from '../BayTooltip';
import DotIcon from '../Icons/DotIcon';

type BadgeColor = 'success' | 'danger' | 'warning' | 'info' | 'secondary' | 'primary' | 'pink' | 'indigo' | 'teal' | 'orange';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BayBadgeProps {
	bg?: BadgeColor | string;
	id?: string;
	text?: BadgeColor | string;
	pill?: boolean;
	/** @deprecated Use size prop instead */
	small?: boolean;
	/** @deprecated Use size prop instead */
	medium?: boolean;
	/** @deprecated Use size prop instead */
	large?: boolean;
	size?: BadgeSize | string;
	border?: boolean;
	noRadius?: boolean;
	bold?: boolean;
	borderColor?: string;
	showDot?: boolean;
	placement?: string;
	overlayClassName?: string;
	showToolTip?: boolean;
	toolTipContent?: ReactNode;
	fullWidth?: boolean;
	isLight?: boolean;
	isDark?: boolean;
	children?: ReactNode;
	styleProps?: CSSProperties;
	textValue?: string | number;
	bgValue?: string | number;
	className?: string;
}

// Arbitrary per-instance colors (bg/text accept any color name, borderColor
// accepts any raw CSS color) can't be a fixed class set, so these stay
// computed in JS and applied as inline style -- same values the emotion
// template used to compute, just assigned directly instead of injected via
// a generated stylesheet.
const borderColorValue = (
	props: Pick<BayBadgeProps, 'border' | 'borderColor' | 'isDark' | 'bg' | 'isLight'>,
): string | undefined => {
	if (!props.border) return undefined;
	if (props.borderColor) return props.borderColor;
	return props.isDark
		? 'var(--bs-dark)'
		: props.bg
			? `var(--bs-${props.bg}${props.isLight ? '-300' : '-700'})`
			: 'var(--bs-gray-100)';
};

const backgroundColorValue = (
	props: Pick<BayBadgeProps, 'bgValue' | 'isLight' | 'isDark' | 'bg'>,
): string => {
	let bgSuffix = '';
	if (props.bgValue) {
		bgSuffix = `-${bgSuffix + props.bgValue}`;
	} else if (props.isLight) {
		bgSuffix = '-100';
	} else {
		bgSuffix = '-800';
	}
	return props.isDark
		? 'var(--bs-dark)'
		: props.bg
			? `var(--bs-${props.bg}${bgSuffix})`
			: 'var(--bs-gray-100)';
};

const colorValue = (
	props: Pick<BayBadgeProps, 'textValue' | 'isLight' | 'text'>,
): string => {
	let textSuffix = '';
	if (props.textValue) {
		textSuffix = `-${textSuffix + props.textValue}`;
	} else if (props.isLight) {
		textSuffix = '-800';
	} else {
		textSuffix = '-100';
	}
	return props.text && props.text !== 'warning'
		? `var(--bs-${props.text}${textSuffix})`
		: props.isLight
			? 'var(--bs-gray-800)'
			: 'var(--bs-light)';
};

const BayBadge: FC<BayBadgeProps> = (props) => {
	const {
		id,
		bg,
		text,
		pill,
		small,
		medium,
		large,
		size,
		border,
		noRadius,
		bold,
		borderColor,
		showDot,
		placement,
		overlayClassName,
		showToolTip,
		toolTipContent,
		fullWidth,
		isLight,
		isDark,
		children,
		styleProps,
		textValue,
		bgValue,
		className,
	} = props;

	// Same priority order the old fontSize() ternary used: small beats
	// medium beats large when more than one is set at once (deprecated
	// booleans and the `size` prop are not meant to be mixed).
	const isSmall = size === 'sm' || small;
	const isMedium = !isSmall && (size === 'md' || medium);
	const isLarge = !isSmall && !isMedium && (size === 'lg' || large);

	const badgeClassName = clsx('bay-badge', className, {
		'bay-badge--sm': isSmall,
		'bay-badge--md': isMedium,
		'bay-badge--lg': isLarge,
		'bay-badge--bold': bold,
		'bay-badge--pill': pill,
		'bay-badge--no-radius': noRadius,
		'bay-badge--full-width': fullWidth,
		'bay-badge--border': border,
	});

	const badgeStyle: CSSProperties = {
		backgroundColor: backgroundColorValue({ bgValue, isLight, isDark, bg }),
		color: colorValue({ textValue, isLight, text }),
		borderColor: borderColorValue({ border, borderColor, isDark, bg, isLight }),
		...styleProps,
	};

	const badge = (
		<span className={badgeClassName} id={id || ''} style={badgeStyle}>
			{showDot && <DotIcon />}
			<span className={showDot ? 'ms-2' : 'ms-0'}>{children}</span>
		</span>
	);

	return showToolTip ? (
		<BayTooltip
			overlay={<span id="tooltip_content">{toolTipContent}</span>}
			overlayClassName={overlayClassName ? overlayClassName : 'bay-badge-tooltip'}
			placement={placement ? placement : 'top'}
		>
			{badge}
		</BayTooltip>
	) : (
		badge
	);
};

export default BayBadge;
